import {
    TouchEvent as ReactTouchEvent,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import useSWR, { mutate } from 'swr';
import { useTranslation } from 'react-i18next';
import {
    Calendar as BigCalendar,
    Navigate,
    ToolbarProps,
    View,
    Views,
} from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './calendar.css';
import { isMobileOnly } from 'react-device-detect';
import { Printer } from 'lucide-react';
import addMonths from 'date-fns/addMonths';
import addWeeks from 'date-fns/addWeeks';
import getDaysInMonth from 'date-fns/getDaysInMonth';
import parseISO from 'date-fns/parseISO';
import startOfMonth from 'date-fns/startOfMonth';
import getISOWeek from 'date-fns/getISOWeek';
import nb from 'date-fns/locale/nb';
import enUS from 'date-fns/locale/en-US';

import { SpinnerIconSVG } from 'src/assets/svg';
import { CalendarIcon } from 'src/components/Icons/icons';
import { useNotification } from 'src/components/Notification/useNotfication';
import container from 'src/container';
import { EventType, getMunduEventType, MunduEvent } from 'src/domain/event';
import {
    OrganizationModuleType,
    canEmployeeReadOrganizationModule,
    isOrganizationModuleEnabled,
    isOrganizationModuleMember,
} from 'src/domain/organization';
import {
    getPrimaryRole,
    isOrganizationAdministrator,
    UserRole,
} from 'src/domain/user';
import { useCurrentOrganization } from 'src/view/hooks/useCurrentOrganization';
import { useSignedInUser } from 'src/view/hooks/useSignedInUser';
import { useCreatorRoleContext } from 'src/view/hooks/useCreatorRoleContext';
import { canManageCalendarEvent } from 'src/utils/eventPermissions';
import { getHttpErrorMessage } from 'src/utils/httpErrorMessage';
import {
    isCalendarEventCreatedByUser,
    isCalendarEventInGroupScope,
} from 'src/utils/calendarEventFilters';
import {
    CalendarNavigationDirection,
    getAdjacentCalendarDate,
} from 'src/utils/calendarNavigation';
import { CalendarNavigation } from './CalendarNavigation';
import { CalendarToolbar } from './CalendarToolbar';
import {
    CalendarAgendaDate,
    CalendarEventContent,
    CalendarMode,
    CalendarSlotEvent,
    calendarFormats,
    calendarLocalizer,
    calendarScrollToTime,
    calendarViews,
    formatCalendarTime,
    formatWeekMonthLabel,
    getActiveView,
    getCalendarEventProps,
    getListFocusDateKey,
    getVisibleRange,
    MyEventsFilter,
} from './calendarModel';
import { EventFormModal } from './EventFormModal';
import { getOriginalOccurrenceStart } from './eventFormModel';
import { ResidentWeekPlan } from './ResidentWeekPlan';
import { ModulePageLayout } from '../components/ModulePageLayout';
import { ModuleUnavailableState } from '../components/ModuleUnavailableState';

const {
    cradle: { munduClient },
} = container;

export const CalendarScreen = () => {
    const { t, i18n } = useTranslation();
    const notify = useNotification();
    const user = useSignedInUser();
    const role = getPrimaryRole(user);
    const isOrganizationAdmin = isOrganizationAdministrator(user);
    const { activeRoleGroupIds } = useCreatorRoleContext();
    const { organizationId, organization, isLoading } =
        useCurrentOrganization();
    // Week and month grids are cramped on phones, start on the day view there.
    const [view, setView] = useState<View>(
        isMobileOnly ? Views.DAY : Views.WEEK,
    );
    const [calendarMode, setCalendarMode] = useState<CalendarMode>('calendar');
    const [myEventsFilter, setMyEventsFilter] =
        useState<MyEventsFilter>('department');
    const [date, setDate] = useState<Date>(new Date());
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState<
        MunduEvent | undefined
    >();
    const [slotStart, setSlotStart] = useState<Date | undefined>();
    const [slotEnd, setSlotEnd] = useState<Date | undefined>();
    const [slideDirection, setSlideDirection] =
        useState<CalendarNavigationDirection>();
    const calendarContainerRef = useRef<HTMLDivElement>(null);
    const swipeStartRef = useRef<{ x: number; y: number }>();
    const swipeLastRef = useRef<{ x: number; y: number }>();

    const culture = i18n.language.startsWith('en') ? 'en-US' : 'nb';
    const calendarLocale = i18n.language.startsWith('en') ? enUS : nb;

    const moduleEnabled = isOrganizationModuleEnabled(
        organization,
        OrganizationModuleType.calendar,
    );
    const isCalendarModuleMember = isOrganizationModuleMember(
        organization,
        OrganizationModuleType.calendar,
        user?.id,
    );
    const canManageCalendar =
        !user?.supportMode && moduleEnabled && isCalendarModuleMember;
    const hasModuleAccess =
        moduleEnabled &&
        (isOrganizationAdmin ||
            role !== UserRole.creator ||
            canEmployeeReadOrganizationModule(
                organization,
                OrganizationModuleType.calendar,
                user?.id,
            ));

    useEffect(() => {
        if (!canManageCalendar && myEventsFilter !== 'department') {
            setMyEventsFilter('department');
        }
    }, [canManageCalendar, myEventsFilter]);

    useEffect(() => {
        if (!canManageCalendar && calendarMode === 'readView') {
            setCalendarMode('calendar');
        }
    }, [calendarMode, canManageCalendar]);

    const activeView = getActiveView(calendarMode, view);
    const { from, to } = getVisibleRange(date, activeView);
    const eventsKey =
        organizationId && hasModuleAccess
            ? ['events', organizationId, from.toISOString(), to.toISOString()]
            : null;

    const { data } = useSWR(eventsKey, (_key, orgId, fromIso, toIso) =>
        munduClient.listEvents({
            organizationId: orgId,
            from: fromIso,
            to: toIso,
            includeInternal: true,
        }),
    );

    const allEvents = useMemo<CalendarSlotEvent[]>(
        () =>
            (data?.data ?? []).map(event => ({
                id: event.occurrenceId ?? event.id,
                title: event.title,
                start: parseISO(event.startDate),
                end: parseISO(event.endDate),
                allDay: event.allDay,
                isCreatedByCurrentUser: isCalendarEventCreatedByUser(
                    event,
                    user?.id,
                ),
                resource: event,
            })),
        [data, user?.id],
    );
    const events = useMemo(() => {
        let visibleEvents = allEvents;

        if (calendarMode === 'readView') {
            visibleEvents = allEvents.filter(
                event =>
                    getMunduEventType(event.resource) !== EventType.internal,
            );
        } else if (calendarMode === 'myEvents') {
            if (myEventsFilter === 'createdByMe') {
                visibleEvents = allEvents.filter(
                    event => event.isCreatedByCurrentUser,
                );
            } else {
                const hasOrganizationScope = isOrganizationAdmin;
                visibleEvents = allEvents.filter(event =>
                    isCalendarEventInGroupScope(
                        event.resource,
                        activeRoleGroupIds,
                        hasOrganizationScope,
                        organization?.groups,
                    ),
                );
            }
        }

        const now = Date.now();
        if (!visibleEvents.some(event => event.end.getTime() <= now)) {
            return visibleEvents;
        }

        const firstCurrentEvent = visibleEvents.reduce<
            CalendarSlotEvent | undefined
        >((first, event) => {
            if (event.end.getTime() <= now) return first;
            if (!first || event.start.getTime() < first.start.getTime()) {
                return event;
            }
            return first;
        }, undefined);

        if (!firstCurrentEvent) return visibleEvents;

        return visibleEvents.map(event =>
            event.id === firstCurrentEvent.id
                ? {
                      ...event,
                      isCurrentBoundary: true,
                      currentBoundaryLabel: t('modules.calendar.views.now', {
                          time: formatCalendarTime(new Date(now)),
                      }),
                  }
                : event,
        );
    }, [
        activeRoleGroupIds,
        allEvents,
        calendarMode,
        isOrganizationAdmin,
        myEventsFilter,
        organization?.groups,
        t,
    ]);
    const selectedCalendarEvent = events.find(
        event =>
            event.id === (selectedEvent?.occurrenceId ?? selectedEvent?.id),
    );
    const canManageSelectedEvent =
        canManageCalendar &&
        (selectedEvent
            ? canManageCalendarEvent(
                  selectedEvent,
                  role,
                  user?.id,
                  isOrganizationAdmin,
              )
            : true);
    const calendarDate =
        activeView === Views.AGENDA ? startOfMonth(date) : date;
    // react-big-calendar treats agenda length as an inclusive day offset
    const listLength =
        activeView === Views.AGENDA
            ? getDaysInMonth(calendarDate) - 1
            : undefined;
    const listFocusDateKey = useMemo(
        () => getListFocusDateKey(events, calendarDate),
        [events, calendarDate],
    );

    const handleCalendarTouchStart = (
        event: ReactTouchEvent<HTMLDivElement>,
    ) => {
        const target = event.target as HTMLElement;
        if (
            event.touches.length !== 1 ||
            slideDirection ||
            target.closest('button, a, input, select, textarea, [role="menu"]')
        ) {
            swipeStartRef.current = undefined;
            swipeLastRef.current = undefined;
            return;
        }

        const touch = event.touches[0];
        const position = { x: touch.clientX, y: touch.clientY };
        swipeStartRef.current = position;
        swipeLastRef.current = position;
    };

    const handleCalendarTouchMove = (
        event: ReactTouchEvent<HTMLDivElement>,
    ) => {
        if (!swipeStartRef.current || event.touches.length !== 1) return;

        const touch = event.touches[0];
        swipeLastRef.current = { x: touch.clientX, y: touch.clientY };
    };

    const resetCalendarSwipe = () => {
        swipeStartRef.current = undefined;
        swipeLastRef.current = undefined;
    };

    const handleCalendarTouchEnd = () => {
        const start = swipeStartRef.current;
        const end = swipeLastRef.current;
        resetCalendarSwipe();

        if (!start || !end || slideDirection) return;

        const horizontalDistance = end.x - start.x;
        const verticalDistance = end.y - start.y;
        const isHorizontalSwipe =
            Math.abs(horizontalDistance) >= 50 &&
            Math.abs(horizontalDistance) > Math.abs(verticalDistance) * 1.25;

        if (!isHorizontalSwipe) return;

        const direction: CalendarNavigationDirection =
            horizontalDistance < 0 ? 'next' : 'previous';
        setSlideDirection(direction);
        setDate(currentDate =>
            getAdjacentCalendarDate(currentDate, activeView, direction),
        );
    };

    useEffect(() => {
        if (activeView !== Views.AGENDA) return undefined;

        const animationFrame = requestAnimationFrame(() => {
            const calendarContainer = calendarContainerRef.current;
            const listContainer =
                calendarContainer?.querySelector<HTMLElement>(
                    '.rbc-agenda-view',
                );

            if (!listContainer) return;

            listContainer.scrollTop = 0;
            if (!listFocusDateKey) return;

            const dateMarker = calendarContainer?.querySelector<HTMLElement>(
                `[data-calendar-date="${listFocusDateKey}"]`,
            );
            const eventRow = dateMarker?.closest<HTMLTableRowElement>('tr');

            if (eventRow) {
                const eventTop =
                    eventRow.getBoundingClientRect().top -
                    listContainer.getBoundingClientRect().top +
                    listContainer.scrollTop;
                listContainer.scrollTop = Math.max(eventTop - 8, 0);
            }
        });

        return () => cancelAnimationFrame(animationFrame);
    }, [activeView, calendarMode, listFocusDateKey, myEventsFilter]);

    useEffect(() => {
        if (!slideDirection) return undefined;

        const timeout = window.setTimeout(
            () => setSlideDirection(undefined),
            250,
        );
        return () => window.clearTimeout(timeout);
    }, [slideDirection]);

    const calendarComponents = useMemo(
        () => ({
            toolbar: (toolbarProps: ToolbarProps<CalendarSlotEvent>) => (
                <CalendarToolbar
                    {...toolbarProps}
                    calendarMode={calendarMode}
                    myEventsFilter={myEventsFilter}
                    onMyEventsFilterChange={setMyEventsFilter}
                    showMyEventsFilter={canManageCalendar}
                />
            ),
            event: CalendarEventContent,
            agenda: {
                date: CalendarAgendaDate,
                event: CalendarEventContent,
            },
        }),
        [calendarMode, canManageCalendar, myEventsFilter],
    );

    const refreshEvents = () => {
        if (eventsKey) mutate(eventsKey);
    };

    const closeForm = () => {
        setIsFormOpen(false);
        setSelectedEvent(undefined);
        setSlotStart(undefined);
        setSlotEnd(undefined);
    };

    const openEventDetails = async (calendarEvent: CalendarSlotEvent) => {
        const listedEvent = calendarEvent.resource;
        const selectionId = listedEvent.occurrenceId ?? listedEvent.id;
        setSelectedEvent(listedEvent);
        setIsFormOpen(true);

        try {
            const currentEvent = await munduClient.getEvent(
                listedEvent.id,
                getOriginalOccurrenceStart(listedEvent),
            );
            setSelectedEvent(selected =>
                selected &&
                (selected.occurrenceId ?? selected.id) === selectionId
                    ? currentEvent
                    : selected,
            );
        } catch (error) {
            const message = await getHttpErrorMessage(
                error,
                t('admin.notifications.genericError'),
            );
            notify(message, true);
        }
    };

    const messages = {
        allDay: t('modules.calendar.views.allDay'),
        previous: t('modules.calendar.views.previous'),
        next: t('modules.calendar.views.next'),
        today: t('modules.calendar.views.today'),
        month: t('modules.calendar.views.month'),
        week: t('modules.calendar.views.week'),
        day: t('modules.calendar.views.day'),
        agenda: t('modules.calendar.views.agenda'),
        date: t('modules.calendar.views.date'),
        time: t('modules.calendar.views.time'),
        event: t('modules.calendar.views.event'),
        noEventsInRange: t('modules.calendar.views.noEventsInRange'),
        showMore: (total: number) =>
            t('modules.calendar.views.showMore', { total }),
    };

    if (isLoading) {
        return (
            <div className="grid justify-center">
                <SpinnerIconSVG className="animate-spin h-8 w-8 text-woodyBrown" />
            </div>
        );
    }

    if (!hasModuleAccess) {
        return (
            <ModuleUnavailableState
                icon={CalendarIcon}
                moduleName={t('modules.calendar.navLabel')}
                reason="notEnabled"
            />
        );
    }

    return (
        <ModulePageLayout
            basePath="/app/calendar"
            canManageSettings={!user?.supportMode && isOrganizationAdmin}
        >
            <div className="w-full flex flex-col gap-4">
                <div
                    ref={calendarContainerRef}
                    className="mundu-calendar w-full bg-white rounded-xl p-3 sm:p-4"
                >
                    <div className="mundu-calendar-mode-bar">
                        <div
                            className="mundu-calendar-tabs"
                            role="tablist"
                            aria-label={t('modules.calendar.tabs.ariaLabel')}
                        >
                            <button
                                type="button"
                                role="tab"
                                aria-selected={calendarMode === 'calendar'}
                                className={
                                    calendarMode === 'calendar'
                                        ? 'mundu-calendar-control-active'
                                        : undefined
                                }
                                onClick={() => setCalendarMode('calendar')}
                            >
                                {t('modules.calendar.tabs.calendar')}
                            </button>
                            <button
                                type="button"
                                role="tab"
                                aria-selected={calendarMode === 'myEvents'}
                                className={
                                    calendarMode === 'myEvents'
                                        ? 'mundu-calendar-control-active'
                                        : undefined
                                }
                                onClick={() => setCalendarMode('myEvents')}
                            >
                                {t('modules.calendar.tabs.myEvents')}
                            </button>
                            {canManageCalendar ? (
                                <button
                                    type="button"
                                    role="tab"
                                    aria-selected={calendarMode === 'readView'}
                                    className={
                                        calendarMode === 'readView'
                                            ? 'mundu-calendar-control-active'
                                            : undefined
                                    }
                                    onClick={() => setCalendarMode('readView')}
                                >
                                    {t('modules.calendar.tabs.readView')}
                                </button>
                            ) : null}
                        </div>
                        {canManageCalendar && calendarMode !== 'readView' && (
                            <div className="mundu-calendar-primary-action">
                                <button
                                    type="button"
                                    className="mundu-calendar-new-event"
                                    onClick={() => {
                                        setSelectedEvent(undefined);
                                        setIsFormOpen(true);
                                    }}
                                >
                                    {t('modules.calendar.newEvent')}
                                </button>
                            </div>
                        )}
                    </div>
                    {calendarMode === 'readView' && canManageCalendar ? (
                        <>
                            <div className="rbc-toolbar">
                                <CalendarNavigation
                                    onToday={() => setDate(new Date())}
                                    onPrevious={() =>
                                        setDate(currentDate =>
                                            addWeeks(currentDate, -1),
                                        )
                                    }
                                    onNext={() =>
                                        setDate(currentDate =>
                                            addWeeks(currentDate, 1),
                                        )
                                    }
                                />
                                <span className="rbc-toolbar-label">
                                    <span>
                                        {formatWeekMonthLabel(
                                            calendarDate,
                                            calendarLocale,
                                        )}
                                    </span>
                                    <span className="mundu-calendar-week-number">
                                        {t(
                                            'modules.calendar.views.weekNumber',
                                            {
                                                week: getISOWeek(calendarDate),
                                            },
                                        )}
                                    </span>
                                </span>
                                <div className="mundu-calendar-toolbar-actions">
                                    <button
                                        type="button"
                                        className="mundu-calendar-print"
                                        onClick={() => window.print()}
                                    >
                                        <Printer aria-hidden="true" />
                                        <span>
                                            {t(
                                                'modules.calendar.readView.printOrSave',
                                            )}
                                        </span>
                                    </button>
                                </div>
                            </div>
                            <ResidentWeekPlan
                                date={calendarDate}
                                events={events}
                                locale={calendarLocale}
                            />
                        </>
                    ) : (
                        <div
                            className={`mundu-calendar-swipe-surface${
                                slideDirection
                                    ? ` mundu-calendar-slide-${slideDirection}`
                                    : ''
                            }`}
                            onTouchStart={handleCalendarTouchStart}
                            onTouchMove={handleCalendarTouchMove}
                            onTouchEnd={handleCalendarTouchEnd}
                            onTouchCancel={resetCalendarSwipe}
                            onAnimationEnd={event => {
                                if (event.currentTarget === event.target) {
                                    setSlideDirection(undefined);
                                }
                            }}
                        >
                            <BigCalendar
                                localizer={calendarLocalizer}
                                culture={culture}
                                formats={calendarFormats}
                                events={events}
                                eventPropGetter={getCalendarEventProps}
                                selected={selectedCalendarEvent ?? null}
                                view={activeView}
                                onView={nextView => {
                                    if (calendarMode === 'calendar') {
                                        setView(nextView);
                                    }
                                }}
                                date={calendarDate}
                                onNavigate={(nextDate, nextView, action) => {
                                    if (
                                        nextView === Views.AGENDA &&
                                        action === Navigate.PREVIOUS
                                    ) {
                                        setDate(currentDate =>
                                            addMonths(currentDate, -1),
                                        );
                                        return;
                                    }

                                    if (
                                        nextView === Views.AGENDA &&
                                        action === Navigate.NEXT
                                    ) {
                                        setDate(currentDate =>
                                            addMonths(currentDate, 1),
                                        );
                                        return;
                                    }

                                    setDate(nextDate);
                                }}
                                views={calendarViews}
                                length={listLength}
                                components={calendarComponents}
                                messages={messages}
                                scrollToTime={calendarScrollToTime}
                                selectable={canManageCalendar}
                                onSelectSlot={slot => {
                                    if (!canManageCalendar) return;

                                    setSelectedEvent(undefined);
                                    setSlotStart(slot.start as Date);
                                    setSlotEnd(slot.end as Date);
                                    setIsFormOpen(true);
                                }}
                                onSelectEvent={openEventDetails}
                                popup
                                style={{
                                    height: isMobileOnly ? '72vh' : '75vh',
                                }}
                            />
                        </div>
                    )}
                </div>
            </div>
            <EventFormModal
                show={isFormOpen}
                event={selectedEvent}
                readOnly={!canManageSelectedEvent}
                readOnlyReason={
                    !canManageCalendar ? 'moduleMembership' : 'ownership'
                }
                initialStart={slotStart}
                initialEnd={slotEnd}
                onClose={closeForm}
                onSaved={() => {
                    refreshEvents();
                    closeForm();
                }}
                onDeleted={() => {
                    refreshEvents();
                    closeForm();
                }}
            />
        </ModulePageLayout>
    );
};
